/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import {
  X,
  Wallet,
  Edit,
  Save,
  AlertCircle,
  CheckCircle,
  HelpCircle,
} from "lucide-react";
import { AdminContext } from "../../context/AdminContext";
import { toast } from "react-toastify";
import axios from "axios";

const BankDetailsModal = ({ isOpen, onClose, caregiverId }) => {
  const { fetchCaregiverById, updateCaregiverById, backendUrl } =
    useContext(AdminContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [caregiver, setCaregiver] = useState(null);
  const [chapaBanks, setChapaBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);

  const [bankForm, setBankForm] = useState({
    bankCode: "",
    bankAccountNumber: "",
    bankAccountName: "",
  });

  // Fetch banks from public API
  useEffect(() => {
    if (!isOpen) return;

    setBanksLoading(true);
    axios
      .get(`${backendUrl}/api/caregiver/profile/banks`)
      .then((res) => {
        if (res.data.success) {
          setChapaBanks(res.data.banks || []);
        }
      })
      .catch((err) => {
        console.error("Could not load banks:", err);
        toast.error("Failed to load Chapa supported banks list");
      })
      .finally(() => setBanksLoading(false));
  }, [isOpen, backendUrl]);

  // Fetch caregiver details when modal opens
  useEffect(() => {
    if (!isOpen || !caregiverId) return;

    const loadCaregiverDetails = async () => {
      setIsLoading(true);
      try {
        const data = await fetchCaregiverById(caregiverId);
        if (data) {
          setCaregiver(data);

          const hasBankInfo = !!(
            data.bankCode ||
            data.bankAccountNumber ||
            data.bankAccountName
          );
          setIsEditing(!hasBankInfo); // Auto-open edit mode if details are missing

          setBankForm({
            bankCode: data.bankCode || "",
            bankAccountNumber: data.bankAccountNumber || "",
            bankAccountName: data.bankAccountName || data.name || "",
          });
        }
      } catch (error) {
        console.error("Failed to load caregiver details:", error);
        toast.error("Failed to retrieve caregiver profile details");
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    loadCaregiverDetails();
  }, [isOpen, caregiverId, fetchCaregiverById, onClose]);

  if (!isOpen) return null;

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => {
      const nextValue =
        name === "bankAccountNumber" ? value.replace(/\D/g, "") : value;
      const next = { ...prev, [name]: nextValue };

      // Autofill account name with caregiver name if not set
      if (!next.bankAccountName && caregiver?.name) {
        next.bankAccountName = caregiver.name;
      }

      return next;
    });
  };

  const handleSave = async () => {
    if (
      !bankForm.bankCode ||
      !bankForm.bankAccountNumber ||
      !bankForm.bankAccountName
    ) {
      toast.error("All bank details fields are required");
      return;
    }

    if (!/^\d{10,20}$/.test(bankForm.bankAccountNumber)) {
      toast.error("Account number must be between 10 and 20 digits");
      return;
    }

    setIsSaving(true);
    try {
      const result = await updateCaregiverById(caregiverId, {
        bankCode: bankForm.bankCode,
        bankAccountNumber: bankForm.bankAccountNumber,
        bankAccountName: bankForm.bankAccountName,
      });

      if (result) {
        // The context already shows "Caregiver updated successfully" toast
        // Now show distinct subaccount result toast
        if (result.subaccountResult) {
          if (result.subaccountResult.status === "success") {
            toast.success(" Chapa subaccount created successfully");
          } else if (result.subaccountResult.status === "failed") {
            toast.error(
              ` Subaccount creation failed: ${result.subaccountResult.message}`,
            );
          }
        }
        setIsEditing(false);
        // Reload details
        const data = await fetchCaregiverById(caregiverId);
        if (data) setCaregiver(data);
      }
    } catch (err) {
      console.error("Failed to update bank details:", err);
      toast.error("Failed to update bank details");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const hasBankInfo = !!(
      caregiver?.bankCode ||
      caregiver?.bankAccountNumber ||
      caregiver?.bankAccountName
    );
    if (!hasBankInfo) {
      onClose();
    } else {
      setIsEditing(false);
      setBankForm({
        bankCode: caregiver.bankCode || "",
        bankAccountNumber: caregiver.bankAccountNumber || "",
        bankAccountName: caregiver.bankAccountName || caregiver.name || "",
      });
    }
  };

  const maskAccountNumber = (acct) => {
    if (!acct || acct.length < 4) return acct;
    return `••••••••${acct.slice(-4)}`;
  };

  const isDetailsSaved = !!(
    caregiver?.bankCode &&
    caregiver?.bankAccountNumber &&
    caregiver?.bankAccountName
  );
  const selectedBankName =
    chapaBanks.find((b) => String(b.id) === bankForm.bankCode)?.name ||
    bankForm.bankCode;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="bg-card dark:bg-dark-surface rounded-2xl sm:rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-gray-100 dark:border-dark-border animate-in fade-in zoom-in-95 duration-200 my-auto">
        {/* Modal Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-dark-border flex justify-between items-center sticky top-0 bg-card dark:bg-dark-surface z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="p-2 sm:p-2.5 bg-primary/10 rounded-xl sm:rounded-2xl flex-shrink-0">
              <Wallet className="text-primary" size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-bold text-text dark:text-dark-text truncate">
                Bank Details Setup
              </h3>
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-muted mt-0.5 truncate">
                Configure caregiver bank details for payouts
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-400 hover:text-text dark:hover:text-dark-text transition-colors flex-shrink-0 ml-2"
          >
            <X size={18} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                Loading caregiver profile...
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {/* Caregiver Name Card */}
              <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-900/40 rounded-xl sm:rounded-2xl flex items-center gap-2 sm:gap-3 border border-gray-100 dark:border-dark-border/50">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs sm:text-sm uppercase flex-shrink-0">
                  {caregiver?.name
                    ? caregiver.name
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 2)
                    : "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-text dark:text-dark-text text-sm truncate">
                    {caregiver?.name}
                  </h4>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-muted mt-0.5 truncate">
                    ID: {caregiver?.id}
                  </p>
                </div>
              </div>

              {/* Account Status Indicator */}
              <div
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border flex items-start gap-2 sm:gap-3 ${
                  isDetailsSaved
                    ? "bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/30 text-emerald-800 dark:text-emerald-400"
                    : "bg-amber-50/50 dark:bg-amber-950/10 border-amber-100 dark:border-amber-900/30 text-amber-800 dark:text-amber-400"
                }`}
              >
                {isDetailsSaved ? (
                  <CheckCircle
                    className="flex-shrink-0 text-emerald-500 dark:text-emerald-400"
                    size={16}
                  />
                ) : (
                  <AlertCircle
                    className="flex-shrink-0 text-amber-500 dark:text-amber-400"
                    size={16}
                  />
                )}
                <div className="text-[11px] sm:text-xs leading-normal min-w-0 flex-1">
                  <p className="font-bold">
                    {isDetailsSaved
                      ? "Bank Details Synced"
                      : "No Bank Details Formed"}
                  </p>
                  <p className="text-gray-600 dark:text-dark-muted mt-0.5">
                    {isDetailsSaved
                      ? "The bank credentials have been correctly synced and are ready for payouts."
                      : "Add the caregiver's bank details to activate subaccount creation for automated payment transfers."}
                  </p>
                </div>
              </div>

              {/* View/Edit Form Section */}
              <div className="space-y-3 sm:space-y-4">
                {!isEditing ? (
                  /* READ-ONLY VIEW MODE */
                  <div className="space-y-3 sm:space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider block">
                        Bank Name
                      </label>
                      <div className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-dark-border text-xs sm:text-sm font-semibold text-text dark:text-dark-text break-words">
                        {selectedBankName || "Not Set"}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider block">
                        Account Number
                      </label>
                      <div className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-dark-border text-xs sm:text-sm font-semibold text-text dark:text-dark-text tracking-wide">
                        {maskAccountNumber(bankForm.bankAccountNumber) ||
                          "Not Set"}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider block">
                        Account Name
                      </label>
                      <div className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-dark-border text-xs sm:text-sm font-semibold text-text dark:text-dark-text break-words">
                        {bankForm.bankAccountName || "Not Set"}
                      </div>
                    </div>

                    <div className="pt-2 flex justify-end">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-primary/10 hover:bg-primary/20 text-primary font-bold rounded-xl text-xs sm:text-sm transition-colors"
                      >
                        <Edit size={14} className="sm:w-4 sm:h-4" />
                        Edit Bank Details
                      </button>
                    </div>
                  </div>
                ) : (
                  /* EDIT MODE FORM */
                  <div className="space-y-3 sm:space-y-4">
                    {/* Bank Selector */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider block">
                        Bank Selection
                      </label>
                      <div className="relative">
                        <select
                          name="bankCode"
                          value={bankForm.bankCode}
                          onChange={handleBankChange}
                          disabled={banksLoading}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 font-medium bg-card dark:bg-dark-surface text-text dark:text-dark-text text-xs sm:text-sm disabled:opacity-60 transition-all"
                        >
                          <option value="">
                            {banksLoading
                              ? "Loading Banks..."
                              : "Select Supported Bank"}
                          </option>
                          {chapaBanks.map((bank) => (
                            <option key={bank.id} value={String(bank.id)}>
                              {bank.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Account Number Input */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider block">
                        Account Number
                      </label>
                      <input
                        type="text"
                        name="bankAccountNumber"
                        value={bankForm.bankAccountNumber}
                        onChange={handleBankChange}
                        placeholder="Enter account number (10-20 digits)"
                        maxLength={20}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 font-medium bg-card dark:bg-dark-surface text-text dark:text-dark-text text-xs sm:text-sm transition-all"
                      />
                    </div>

                    {/* Account Holder Name Input */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] sm:text-xs font-bold text-gray-500 dark:text-dark-muted uppercase tracking-wider block">
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        name="bankAccountName"
                        value={bankForm.bankAccountName}
                        onChange={handleBankChange}
                        placeholder="Uses profile name by default"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 font-medium bg-card dark:bg-dark-surface text-text dark:text-dark-text text-xs sm:text-sm transition-all"
                      />
                      <p className="text-[10px] text-gray-400 dark:text-dark-muted mt-1 leading-normal">
                        Must match the registered bank account name exactly to
                        avoid payment routing issues during processing.
                      </p>
                    </div>

                    {/* Form Controls */}
                    <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-gray-100 dark:border-dark-border mt-4 sm:mt-6">
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs sm:text-sm disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={
                          isSaving ||
                          banksLoading ||
                          !bankForm.bankCode ||
                          !bankForm.bankAccountNumber ||
                          !bankForm.bankAccountName
                        }
                        className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-white rounded-xl font-bold transition-all shadow-lg text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 ${
                          isSaving ||
                          !bankForm.bankCode ||
                          !bankForm.bankAccountNumber ||
                          !bankForm.bankAccountName
                            ? "bg-gray-400 cursor-not-allowed shadow-none"
                            : "bg-primary hover:bg-primary/95 shadow-primary/20"
                        }`}
                      >
                        {isSaving ? (
                          <>
                            <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="hidden xs:inline">Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save size={14} className="sm:w-4 sm:h-4" />
                            <span>Save Details</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankDetailsModal;
